// PRICE-FIRST: a paid tap shows its own price before anything is charged, and sends that price (expectCredits) so the
// server refuses a dearer one. Under the user's auto-OK amount (Settings > Spending), the tap on "Make it · 15" is the
// yes; over it, and for any video, a confirm sheet asks first. A refused yes (the price went up) never just fails:
// the sheet opens (or stays open) with the new price and asks again. Rules in src/price.ts.
import { useState, type ReactNode } from "react";
import type { PaidOutcome } from "../actions";
import { api } from "../api";
import { goWords, repriced, tapRule, withPrice, type PriceStep, type Prices, type StepPrice } from "../price";
import { useStore, toast } from "../store";
import { fmtCredits, Icon, Sheet, Spinner, useLoad } from "./bits";
import { Notice } from "./Notice";

/**
 * The project's step prices (with `sceneId`: the scene's frame, clip and sounds too), reloaded when `key` changes
 * (pass what the price depends on: the shot, the model picks).
 */
export function usePrices(projectId: string | null | undefined, sceneId?: string | null, key: unknown[] = [], model?: string | null) {
  const { data, error, reload } = useLoad<Prices | null>(() => (projectId ? api.prices(projectId, sceneId ?? null, model ?? null) : Promise.resolve(null)), [projectId, sceneId ?? null, model ?? null, ...key]);
  return { prices: data, error, reload };
}

/** One step's price from a project's prices (undefined while loading). */
export function usePrice(projectId: string | null | undefined, step: PriceStep | null, sceneId?: string | null, key: unknown[] = [], model?: string | null): StepPrice | null | undefined {
  const { prices } = usePrices(step ? projectId : null, sceneId, key, model);
  if (!step) return null;
  return prices ? (prices.prices[step] ?? null) : undefined;
}

export type Ask = {
  /** the sheet's title: "Make scene 2's frame?" */
  title: string;
  /** one plain line about what it makes */
  lede: string;
  /** the go button's verb: "Make it", "Try again" */
  go: string;
};

/**
 * The confirm sheet for one paid step: the price, a line on what it makes, the balance, and "Go for N". On a refused
 * yes it shows the new price and asks again; on any other failure it says why and stays open.
 */
export function PriceSheet({ ask, price, notice, run, onClose, onDone }: { ask: Ask; price: StepPrice | null | undefined; notice?: string | null; run: (expectCredits: number) => Promise<PaidOutcome>; onClose: () => void; onDone?: (r: Extract<PaidOutcome, { kind: "ok" }>) => void }) {
  const balance = useStore((s) => s.balance);
  const [now, setNow] = useState<{ price: StepPrice; text: string } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [going, setGoing] = useState(false);
  const cur = now?.price ?? price;
  const credits = cur?.credits ?? null;
  const go = async () => {
    if (credits === null || !cur) return;
    setGoing(true);
    setFailure(null);
    const r = await run(credits);
    setGoing(false);
    if (r.kind === "ok") {
      onDone?.(r);
      return onClose();
    }
    if (r.kind === "reprice") setNow({ price: repriced(cur, r.price.credits), text: r.text });
    else setFailure(r.text);
  };
  const changed = now?.text ?? notice ?? null;
  return (
    <Sheet title={ask.title} onClose={onClose}>
      <div className="sheet-body confirm price-sheet">
        {cur === undefined && <Spinner label="Pricing it" />}
        {cur === null && <p className="form-error" role="alert">Couldn't price this right now. Try again in a moment.</p>}
        {cur?.credits === null && cur.why && <p className="form-error" role="alert">{cur.why}</p>}
        {changed && <Notice tone="warning" text={`${changed} Go ahead?`} code="CONFIRM_REQUIRED" />}
        {credits !== null && (
          <>
            <p className="confirm-num">
              {fmtCredits(credits)} <span>credits</span>
            </p>
            <p>{ask.lede}</p>
            <p className="muted">
              {cur?.clips ? "It's video, so it needs your yes. " : ""}You have {fmtCredits(balance)}. If it fails, the credits come back.
            </p>
          </>
        )}
        {failure && <Notice tone="error" text={`Couldn't start it. ${failure}`} />}
        <div className="confirm-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Not yet
          </button>
          <button data-action="price.go" type="button" className="btn btn-primary" disabled={credits === null || going} data-autofocus onClick={() => void go()}>
            {going ? "Starting" : goWords(ask.go, credits)}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/**
 * A button that costs credits, with its price on it ("Make it · 15", or an icon with a small "15"). Under the user's
 * auto-OK amount the tap starts it at that price; over it (or video) the confirm sheet asks first. No price yet: it
 * waits (nothing paid starts without one).
 */
export function PaidButton({
  price,
  label,
  ask,
  run,
  dataAction,
  className = "btn btn-quiet btn-small",
  icon,
  iconOnly = false,
  ariaLabel,
  disabled = false,
  onDone,
  children,
}: {
  price: StepPrice | null | undefined;
  label: string;
  ask: Ask;
  run: (expectCredits: number) => Promise<PaidOutcome>;
  dataAction?: string;
  className?: string;
  icon?: string;
  /** an icon button (redo): the price shows as a small tag beside the icon */
  iconOnly?: boolean;
  ariaLabel?: string;
  disabled?: boolean;
  onDone?: (r: Extract<PaidOutcome, { kind: "ok" }>) => void;
  children?: ReactNode;
}) {
  const [sheet, setSheet] = useState<{ notice: string | null; price: StepPrice } | null>(null);
  const [busy, setBusy] = useState(false);
  const rule = tapRule(price);
  const credits = price?.credits ?? null;
  const tap = async () => {
    if (!price || rule === "wait") return;
    if (rule === "ask") return setSheet({ notice: null, price });
    setBusy(true);
    const r = await run(price.credits!);
    setBusy(false);
    if (r.kind === "ok") return onDone?.(r);
    // the price went up since it was shown: ask with the new one; anything else is a plain sentence
    if (r.kind === "reprice") setSheet({ notice: r.text, price: repriced(price, r.price.credits) });
    else toast(`Couldn't start it. ${r.text}`, "bad");
  };
  const words = credits === null ? (price?.why ?? "Pricing it") : `${ariaLabel ?? label} for ${fmtCredits(credits)} credits${rule === "ask" ? ". Asks first" : ""}`;
  return (
    <>
      <button
        data-action={dataAction}
        type="button"
        className={`${className} paid-btn${iconOnly ? " paid-icon" : ""}`}
        disabled={disabled || busy || rule === "wait"}
        aria-label={words}
        title={iconOnly ? words : undefined}
        onClick={() => void tap()}
      >
        {icon && <Icon name={icon} size={iconOnly ? 18 : 14} />}
        {iconOnly ? (
          <span className="paid-tag">{credits === null ? "…" : fmtCredits(credits)}</span>
        ) : (
          <>
            {icon ? " " : ""}
            {busy ? "Starting" : withPrice(label, credits)}
            {children}
          </>
        )}
      </button>
      {sheet && <PriceSheet ask={ask} price={sheet.price} notice={sheet.notice} run={run} onClose={() => setSheet(null)} onDone={onDone} />}
    </>
  );
}

